import React, { useState, useEffect } from 'react';
import { Modal } from '@smartphone-tycoon/shared-ui';
import { Button } from '@smartphone-tycoon/shared-ui';
import { Card } from '@smartphone-tycoon/shared-ui';
import type { ActiveEvent } from '@/types/dynamicEvents';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';

interface ReactiveEventModalProps {
  event: ActiveEvent | null;
  isOpen: boolean;
  playerCash?: number;
  error?: string | null;
  onChoice: (choiceId: string, dontAskAgain?: boolean) => void | Promise<void>;
  onClose: () => void;
  onClearError?: () => void;
}

export function ReactiveEventModal({
  event,
  isOpen,
  playerCash = 0,
  error,
  onChoice,
  onClose,
  onClearError
}: ReactiveEventModalProps) {
  const [selectedChoice, setSelectedChoice] = useState<string | null>(null);
  const [dontAskAgain, setDontAskAgain] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedChoice(null);
      setDontAskAgain(false);
      setIsSubmitting(false);
      // Clear any previous error when modal opens
      if (onClearError) onClearError();
    } else {
      setIsSubmitting(false);
    }
  }, [isOpen, onClearError]);

  useEffect(() => {
    if (!event) {
      return;
    }
    setSelectedChoice(null);
    setDontAskAgain(false);
    setIsSubmitting(false);
    if (onClearError) onClearError();
  }, [event?.event.id, onClearError]);

  useEffect(() => {
    if (error) {
      setIsSubmitting(false);
    }
  }, [error]);

  if (!event || !isOpen) return null;

  const { event: dynamicEvent } = event;
  const choices = dynamicEvent.choices || [];

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-theme-text-primary border-l-4 border-theme-error bg-theme-bg-secondary';
      case 'high': return 'text-theme-text-primary border-l-4 border-theme-warning bg-theme-bg-secondary';
      case 'medium': return 'text-theme-text-primary border-l-4 border-theme-warning bg-theme-bg-secondary';
      case 'low': return 'text-theme-text-primary border-l-4 border-theme-accent bg-theme-bg-secondary';
      default: return 'text-theme-text-secondary border-theme-border bg-theme-bg-secondary';
    }
  };

  const formatEffects = (effects: Record<string, number>, excludeCash = false) => {
    const formatted = [];
    if (effects.cash && !excludeCash) {
      formatted.push(`💰 ${effects.cash > 0 ? '+' : ''}${formatCurrency(Math.abs(effects.cash)).replace('$', '')}`);
    }
    if (effects.reputation) {
      formatted.push(`⭐ ${effects.reputation > 0 ? '+' : ''}${effects.reputation}`);
    }
    if (effects.marketShare) {
      formatted.push(`📊 ${effects.marketShare > 0 ? '+' : ''}${(effects.marketShare * 100).toFixed(1)}%`);
    }
    if (effects.researchPoints) {
      formatted.push(`🔬 ${effects.researchPoints > 0 ? '+' : ''}${effects.researchPoints}`);
    }
    return formatted;
  };

  const handleConfirm = () => {
    if (!selectedChoice || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      Promise.resolve(onChoice(selectedChoice, dontAskAgain))
        .finally(() => {
          setIsSubmitting(false);
        });
    } catch (choiceError) {
      setIsSubmitting(false);
      throw choiceError;
    }
  };

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      className="max-w-2xl"
      data-testid="phase-progression-modal"
    >
      <div className="flex flex-col h-full">
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-6 pb-0">
          <div className={`rounded-lg p-4 mb-4 ${getSeverityColor(dynamicEvent.severity)}`}>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl font-bold text-theme-text-primary">{dynamicEvent.title}</h2>
            </div>

            {/* Only show metadata for non-development events */}
            {dynamicEvent.category !== 'development' && (
              <div className="flex items-center space-x-4 text-sm">
                <span className="capitalize">{dynamicEvent.category}</span>
                <span>•</span>
                <span className="capitalize">{dynamicEvent.severity} Impact</span>
                {dynamicEvent.historical && (
                  <>
                    <span>•</span>
                    <span className="flex items-center">
                      📚 Historical Event
                    </span>
                  </>
                )}
              </div>
            )}

            {/* Show different metadata for development events */}
            {dynamicEvent.category === 'development' && dynamicEvent.historical && (
              <div className="flex items-center space-x-4 text-sm">
                <span className="flex items-center">
                  📚 Historical Event
                </span>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="mb-4">
            <p className="text-theme-text-secondary leading-relaxed">
              {dynamicEvent.description}
            </p>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 min-h-0">
          {/* Error Display */}
          {error && (
            <div className="mb-4 p-4 bg-red-900/20 border border-red-500 rounded-lg" data-testid="phase-error">
              <div className="flex items-start space-x-2">
                <span className="text-red-500 text-lg">⚠️</span>
                <div>
                  <p className="text-red-400 font-medium">{error}</p>
                  <p className="text-sm text-red-300/80 mt-1">
                    You may need to hire more employees or wait for resources before proceeding.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Choices */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-theme-text-primary mb-3">Choose your response:</h3>

            {choices.map((choice) => {
              const effects = choice.outcome.immediateEffects;
              const isSelected = selectedChoice === choice.id;
              const costRequirement = choice.costRequirement?.cash || 0;
              const canAfford = costRequirement === 0 || playerCash >= costRequirement;
              const isDisabled = isSubmitting || !canAfford;

              return (
                <Card
                  key={choice.id}
                  data-testid={`choice-${choice.id}`}
                  className={`p-4 transition-all border-2 ${
                    !canAfford
                      ? 'border-theme-error bg-theme-bg-secondary opacity-60 cursor-not-allowed'
                      : isSubmitting
                        ? 'border-theme-border bg-theme-bg-secondary opacity-70 cursor-not-allowed'
                        : isSelected
                          ? 'border-theme-accent bg-theme-bg-secondary cursor-pointer hover:shadow-md'
                          : 'border-theme-border hover:border-theme-border cursor-pointer hover:shadow-md'
                  }`}
                  onClick={() => !isDisabled && setSelectedChoice(choice.id)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium text-theme-text-primary mb-1">
                        {choice.text}
                      </h4>
                      {choice.description && (
                        <p className="text-sm text-theme-text-secondary mb-2">
                          {choice.description}
                        </p>
                      )}

                      {costRequirement > 0 && (
                        <div className="mb-2">
                          <span className={`text-sm px-2 py-1 rounded ${
                            canAfford
                              ? 'bg-theme-bg-tertiary text-theme-text-primary border border-theme-warning'
                              : 'bg-theme-bg-tertiary text-theme-text-primary border border-theme-error'
                          }`}>
                            💰 Cost: {formatCurrency(costRequirement)}
                            {!canAfford && ' (Insufficient funds)'}
                          </span>
                        </div>
                      )}

                      {effects && (
                        <div className="flex flex-wrap gap-2">
                          {formatEffects(effects, costRequirement > 0).map((effect, index) => (
                            <span
                              key={index}
                              className="text-xs px-2 py-1 bg-theme-bg-tertiary rounded-full"
                            >
                              {effect}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="ml-4 flex-shrink-0">
                      <div className={`w-4 h-4 rounded-full border-2 ${
                        isSelected
                          ? 'border-theme-accent bg-theme-accent'
                          : 'border-theme-border'
                      }`}>
                        {isSelected && (
                          <div className="w-full h-full rounded-full bg-white transform scale-50"></div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Don't Ask Again Checkbox - Only for phase progression events */}
          {dynamicEvent.category === 'development' && dynamicEvent.id.includes('_complete') && (
            <div className="mt-4 p-4 bg-theme-bg-secondary rounded-lg border border-theme-border">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={dontAskAgain}
                  onChange={(e) => setDontAskAgain(e.target.checked)}
                  disabled={isSubmitting}
                  data-testid="dont-ask-again"
                  className="mt-1 h-4 w-4 text-theme-accent border-theme-border rounded focus:ring-theme-accent"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-theme-text-secondary">
                    Always use the same choice. Don't ask me next time.
                  </span>
                  <p className="text-xs text-theme-text-muted mt-1">
                    You can configure your default choices and re-enable these prompts in the settings menu.
                  </p>
                </div>
              </label>
            </div>
          )}
        </div>

        {/* Fixed Footer */}
        <div className="flex-shrink-0 flex justify-end space-x-3 p-6 pt-4 border-t border-theme-border bg-theme-bg-content">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Close
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSubmitting || !selectedChoice || (selectedChoice && choices.find(c => c.id === selectedChoice)?.costRequirement?.cash && playerCash < (choices.find(c => c.id === selectedChoice)?.costRequirement?.cash || 0))}
            data-testid="confirm-choice"
            className="min-w-24"
          >
            Confirm Choice
          </Button>
        </div>
      </div>
    </Modal>
  );
}
